import styled from '@emotion/styled';
import Gap from '~/components/Gap';
import Text from '~/components/Text';
import DownloadIcon from '~/icons/DownloadIcon';
import { fmPrice } from '~/utils/amount';
import { formatDate } from '~/utils/datetime';
import { openInNewTab } from '~/utils/utils';
import { useInvoiceListContext } from '../hooks/useInvoiceListContext';
import InvoiceDueStatus from '../InvoiceDueStatus';
import InvoiceStatusPills from './InvoiceStatusPills';
import InvoiceContextMenu from './InvoiceContextMenu';

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: var(--neut6);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--neut5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface ICellContentProps {
  invoiceReferenceId: string;
  identifier: string;
}

const defaultValueDash = '--';

const CellContent = ({ invoiceReferenceId, identifier }: ICellContentProps) => {
  const { listData, onOpenViewInvoiceModal } = useInvoiceListContext();
  const thisInvoiceData = listData[invoiceReferenceId];

  if (typeof thisInvoiceData === 'undefined') {
    return null;
  }

  if (identifier === 'invoice_number') {
    return (
      <div
        className="cursor-pointer"
        onClick={() => {
          onOpenViewInvoiceModal(thisInvoiceData.invoice_uuid);
        }}
      >
        <Text className="bold hover-underline">{thisInvoiceData.invoice_number}</Text>
        <Gap t={2} />
        <Text size="xs" variant="secondary" className="bold">
          {thisInvoiceData.buyer_company_name}
        </Text>
      </div>
    );
  }

  if (identifier === 'created_on' || identifier === 'due_date') {
    const formattedDate = thisInvoiceData[identifier]
      ? formatDate(thisInvoiceData[identifier])
      : null;

    return (
      <div>
        <Text>{formattedDate}</Text>
        {identifier === 'due_date' && <InvoiceDueStatus invoiceData={thisInvoiceData} />}
      </div>
    );
  }

  if (identifier === 'amount') {
    return <Text>{fmPrice(thisInvoiceData.currency, thisInvoiceData.total_amount)}</Text>;
  }

  if (identifier === 'status') {
    return <InvoiceStatusPills invoiceData={thisInvoiceData} showActions />;
  }

  if (identifier === 'download') {
    const handleDownloadClick = () => {
      if (thisInvoiceData.pdf_url) {
        openInNewTab(thisInvoiceData.pdf_url);
      }
    };

    return (
      <ActionsWrapper>
        <DownloadButton
          onClick={handleDownloadClick}
          disabled={!thisInvoiceData.pdf_url}
          title="Download PDF"
        >
          <DownloadIcon width={16} height={16} />
        </DownloadButton>
      </ActionsWrapper>
    );
  }

  if (identifier === 'actions') {
    return (
      <div className="center">
        <InvoiceContextMenu invoiceReferenceId={invoiceReferenceId} />
      </div>
    );
  }

  return <p data-text-type="main-text">{defaultValueDash}</p>;
};

export default CellContent;
